import { useEffect, useState } from 'react';
import type { CorrecaoTreinamento } from '@fiodecobre/shared';
import { api, ApiError } from '../../lib/api';
import type { PerguntaEquipe } from './videos';

// Questionario depois do video (decisao do Ruan, 29/09/2026): SO REGISTRA, nao trava o proximo
// video. As perguntas chegam do banco SEM o gabarito (0304); a tela manda os indices e QUAIS
// perguntas respondeu, e o servidor corrige — se o questionario mudou no meio, ele recusa (409).
//
// Certa/errada NAO se diz por cor (daltonismo verde/vermelho — memoria user_daltonismo): a
// alternativa certa ganha o par `#fde68a` + `#000` E o rotulo escrito "Resposta certa"; a marcada
// errada ganha o rotulo "Sua resposta".

type UltimaTentativa = { acertos: number; total: number; criado_em: string };

const SELO_CERTA = { background: '#fde68a', color: '#000' };

function dataHora(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

export function Questionario({ slug, perguntas: lista }: { slug: string; perguntas: PerguntaEquipe[] }) {
  const perguntas = lista.length > 0 ? lista : undefined;
  const [respostas, setRespostas] = useState<(number | null)[]>([]);
  const [correcao, setCorrecao] = useState<CorrecaoTreinamento | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ultima, setUltima] = useState<UltimaTentativa | null>(null);

  useEffect(() => {
    setRespostas(perguntas ? perguntas.map(() => null) : []);
    setCorrecao(null);
    setErro(null);
    setUltima(null);
    if (!perguntas) return;
    let vivo = true;
    api.get<Record<string, UltimaTentativa>>('/api/treinamento/minhas')
      .then((r) => { if (vivo) setUltima(r[slug] ?? null); })
      .catch(() => { /* sem o historico a tela funciona igual */ });
    return () => { vivo = false; };
  }, [slug, perguntas]);

  if (!perguntas) return null;

  const faltam = respostas.filter((r) => r === null).length;

  async function enviar() {
    if (faltam > 0 || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      const r = await api.post<CorrecaoTreinamento>('/api/treinamento/respostas', {
        video_slug: slug, pergunta_ids: perguntas!.map((p) => p.id), respostas,
      });
      setCorrecao(r);
      setUltima({ acertos: r.acertos, total: r.total, criado_em: new Date().toISOString() });
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível enviar. Tente de novo.');
    } finally {
      setEnviando(false);
    }
  }

  function refazer() {
    setRespostas(perguntas!.map(() => null));
    setCorrecao(null);
    setErro(null);
  }

  return (
    <section className="mt-8 border-t border-gray-200 pt-6" data-questionario={slug}>
      {/* Tamanhos em px fixos a pedido dele (29/09/2026: "aumente 2px em cada"). */}
      <h3 className="text-[17.75px] font-bold text-gray-800">Questionário</h3>
      <p className="text-[14.25px] text-gray-600 mt-1">
        {perguntas.length} perguntas sobre o vídeo. Marque uma resposta em cada uma.
      </p>
      {ultima && !correcao && (
        <p className="text-sm text-gray-700 mt-2">
          Você já respondeu em {dataHora(ultima.criado_em)}: acertou <b>{ultima.acertos} de {ultima.total}</b>.
          Pode responder de novo quando quiser.
        </p>
      )}

      {correcao && (
        <div className="mt-4 rounded-md px-4 py-3 font-medium" style={SELO_CERTA}>
          Você acertou {correcao.acertos} de {correcao.total}.
          {correcao.acertos < correcao.total && ' Veja abaixo a resposta certa das que você errou.'}
        </div>
      )}

      <ol className="mt-4 space-y-5">
        {perguntas.map((p, i) => {
          const marcada = respostas[i];
          const errou = correcao && marcada !== correcao.certas[i];
          return (
            <li key={p.id}>
              <div className="font-medium text-gray-800 text-[16px]">
                {i + 1}. {p.pergunta}
                {correcao && <span className="ml-2 text-sm font-bold">{errou ? '— errou' : '— acertou'}</span>}
              </div>
              <div className="mt-2 space-y-1">
                {p.alternativas.map((alt, j) => {
                  const ehCerta = correcao !== null && j === correcao.certas[i];
                  const ehMarcadaErrada = correcao !== null && j === marcada && !ehCerta;
                  return (
                    <label
                      key={j}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border text-[14.25px] ${
                        correcao ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50'
                      } ${marcada === j ? 'border-blue-600' : 'border-gray-200'}`}
                      style={ehCerta && errou ? SELO_CERTA : undefined}
                    >
                      <input
                        type="radio"
                        name={`q-${slug}-${i}`}
                        checked={marcada === j}
                        disabled={correcao !== null}
                        onChange={() => setRespostas((rs) => rs.map((r, k) => (k === i ? j : r)))}
                      />
                      <span className="flex-1">{alt}</span>
                      {ehCerta && errou && <span className="text-xs font-bold">Resposta certa</span>}
                      {ehMarcadaErrada && <span className="text-xs font-bold">Sua resposta</span>}
                    </label>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        {correcao ? (
          <button type="button" onClick={refazer} className="btn-primario px-4 py-2 rounded-md">
            Responder de novo
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={enviar}
              disabled={faltam > 0 || enviando}
              className="btn-primario px-4 py-2 rounded-md disabled:opacity-50"
            >
              {enviando ? 'Enviando...' : 'Enviar respostas'}
            </button>
            {faltam > 0 && (
              <span className="text-sm text-gray-600">
                Falta{faltam > 1 ? 'm' : ''} {faltam} pergunta{faltam > 1 ? 's' : ''} para responder.
              </span>
            )}
          </>
        )}
        {erro && <span className="text-sm font-bold text-gray-800">{erro}</span>}
      </div>
    </section>
  );
}
